import { ComponentsManager, Component, EventBus } from '../core/smpl-components.js'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { ScrollToPlugin } from 'gsap/ScrollToPlugin'

gsap.registerPlugin(ScrollTrigger)
gsap.registerPlugin(ScrollToPlugin)

/**
 * AnchorScroller handles smooth in-page anchor navigation. It listens for clicks on links with a hash target,
 * validates that the link points to the current page, finds the matching anchor element, and scrolls
 * to it with GSAP.
 *
 * If the target is inside a pinned ScrollTrigger section, it calculates the
 * correct global scroll position for that pinned context.
 *
 * Optional:
 * scope: root element to listen for click events (defaults to document)
 * onBeforeScroll: callback invoked before scrolling with { link, target, href }
 *
 * Example:
 * this.anchorScroller = new AnchorScroller({
 * 	scope: this.element,
 * 	onBeforeScroll: () => {
 * 		if (!this.overlayVisible) return
 * 		this.hideOverlay()
 * 	},
 * })
 *
 * this.anchorScroller.bind()
 **/
class AnchorScroller {
	constructor({ scope = document, onBeforeScroll = null } = {}) {
		this.scope = scope
		this.onBeforeScroll = onBeforeScroll
		this.bndClick = this.onClick.bind(this)
	}

	bind() {
		this.scope.addEventListener('click', this.bndClick, true)
	}

	unbind() {
		this.scope.removeEventListener('click', this.bndClick, true)
	}

	onClick(event) {
		const link = event.target.closest('a[href]')
		if (!link) return

		const url = new URL(link.getAttribute('href'), window.location.href)

		const samePage = url.origin === window.location.origin && url.pathname.replace(/\/+$/, '') === window.location.pathname.replace(/\/+$/, '')

		if (!samePage || !url.hash || url.hash === '#') return

		event.preventDefault()
		event.stopPropagation()

		const id = decodeURIComponent(url.hash.slice(1))
		const target = document.querySelector(`a[name="${id}"], #${CSS.escape(id)}`)
		if (!target) return

		if (typeof this.onBeforeScroll === 'function') {
			this.onBeforeScroll({ link, target, href: url })
		}

		const st = getParentScrollTrigger(target)

		if (st?.pin) {
			const sectionHeight = st.pin.offsetHeight
			const sectionStart = st.start - (sectionHeight - window.innerHeight)
			const anchorOffset = getOffsetToParent(target, st.pin)
			gsap.to(window, {
				scrollTo: { y: sectionStart + anchorOffset, autoKill: false },
				duration: 1,
				ease: 'power2.inOut',
			})
			return
		}

		const y = target.getBoundingClientRect().top + window.scrollY
		gsap.to(window, { scrollTo: { y, autoKill: false }, duration: 1, ease: 'power2.inOut' })
	}
	destroy() {
		this.unbind()
	}
}
export { AnchorScroller }

function getParentScrollTrigger(el) {
	return ScrollTrigger.getAll().find((st) => {
		return st.pin && st.pin.contains(el)
	})
}

function getOffsetToParent(el, parent) {
	const elRect = el.getBoundingClientRect()
	const parentRect = parent.getBoundingClientRect()

	return elRect.top - parentRect.top
}
